/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, March 2020
 */
import { TaskStatus } from './constants';

/**
 * A JSON-compliant representation of a task.
 */
export interface ITaskJSON<T> {
    /**
     * Data associated with the task.
     */
    data: T;
    /**
     * Date in which the task was created as a Unix timestamp.
     */
    timestamp: number;
    /**
     * The task status.
     */
    status: TaskStatus;
    /**
     * The stack trace of the error that made the task fail (if any).
     */
    stackTrace?: string;
}

/**
 * Task options.
 */
export interface ITaskOptions {
    /**
     * The status of the task.
     */
    status?: TaskStatus;
    /**
     * The date in which the task was created as a Unix timestamp.
     */
    timestamp?: number;
    /**
     * The stack trace of the error that made the task fail (if any).
     */
    stackTrace?: string;
}

/**
 * A Queue task.
 */
export class Task<T> {
    public status: TaskStatus;
    public timestamp: number;
    public stackTrace?: string;

    /**
     * Create a task from it's JSON representation (i.e. the one returned by task.toJSON())
     *
     * @param json JSON representation of the task.
     * @returns A task object with the data in the JSON.
     */
    public static fromJSON<T>(json: ITaskJSON<T>): Task<T> {
        return new Task(json.data, {
            status: json.status,
            timestamp: json.timestamp,
            stackTrace: json.stackTrace
        });
    }

    /**
     * Create a task object.
     *
     * @param data The data associated with the task.
     * @param options Task options.
     */
    constructor(public data: T, options?: ITaskOptions) {
        this.status = options?.status ?? TaskStatus.IDLE;
        this.timestamp = options?.timestamp ?? Date.now();
        this.stackTrace = options?.stackTrace;
    }

    /**
     * Check if a task has gone stale.
     *
     * @param maxAge Maximum life time for the task in milliseconds.
     * @returns True if the task has gone stale.
     */
    public isStale(maxAge: number): boolean {
        const life = Date.now() - this.timestamp;
        return life > maxAge;
    }

    /**
     * Serialize a the task into a JSON-compliant representation.
     *
     * @returns JSON-compliant object representing the task.
     */
    public toJSON(): ITaskJSON<T> {
        return {
            data: this.data,
            timestamp: this.timestamp,
            status: this.status,
            stackTrace: this.stackTrace
        };
    }
}



// WEBPACK FOOTER //
// ./src/packages/core/lib/queue/Task.ts